import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function MyPasswordDialog({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setShow(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!currentPassword) {
      toast.error("Enter your current password.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Your password has been updated.");
      handleClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="border-white/10 bg-[#0d0f12] text-white shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] sm:rounded-2xl" data-testid="my-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-lg">
            <KeyRound className="h-4 w-4 text-[#00FF66]" />
            Change my password
          </DialogTitle>
          <DialogDescription className="text-white/45">
            Confirm your current password, then choose a new one. You&apos;ll stay signed in on this device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Current password</label>
            <input
              type={show ? "text" : "password"}
              name="vektor-current-pwd"
              autoComplete="new-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Your current password"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
              data-testid="my-password-current-input"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">New password</label>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                name="new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                placeholder="min. 8 characters"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 pr-11 text-sm text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
                data-testid="my-password-new-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors duration-200 hover:text-white"
                data-testid="my-password-toggle-visibility"
                aria-label={show ? "Hide passwords" : "Show passwords"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-white/30">
              <ShieldCheck className="h-3 w-3" />
              Stored as a bcrypt hash — we can never see your password.
            </p>
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="rounded-full border border-white/15 px-5 py-2 text-xs font-bold text-white/60 transition-colors duration-200 hover:text-white"
            data-testid="my-password-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 rounded-full bg-[#00FF66] px-5 py-2 text-xs font-bold text-black disabled:opacity-60"
            data-testid="my-password-save"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Update password
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
